import type { AnyFormApi } from '@tanstack/vue-form'

import { clearEditedFormErrors } from '~/utils/clearEditedFormErrors'
import { findChangedFormFields } from '~/utils/findChangedFormFields'
import { snapshotFormValues } from '~/utils/snapshotFormValues'

/**
 * Clears a field's blur or submit verdict when the user edits it: TanStack keeps a verdict until
 * the next validation phase, so without this a field being fixed keeps the stale message.
 * Returns the unsubscribe function.
 */
export const subscribeFormEditClearing = <TFormData extends object>(
  form: AnyFormApi,
  defaultValues: TFormData,
  readValues: () => TFormData,
  clearFieldError: (field: keyof TFormData & string) => void
): (() => void) => {
  let previousValues: TFormData = snapshotFormValues(defaultValues)
  let clearing = false

  const subscription = form.store.subscribe(() => {
    if (clearing) return

    const current = readValues()
    const previous = previousValues
    const edited = findChangedFormFields(previous, current)
    previousValues = snapshotFormValues(current)

    if (edited.length > 0) {
      // setMeta writes back into the same store; the guard keeps that write from re-entering this listener.
      clearing = true
      try {
        clearEditedFormErrors(form, edited, previous, current, clearFieldError)
      } finally {
        clearing = false
      }
    }
  })

  return () => {
    subscription.unsubscribe()
  }
}
